import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchHealth, fetchStats, updateFocus } from "../api/stats";
import type { HealthStatus, RequestState, Stats } from "../types";

export const useStats = () => {
	const queryClient = useQueryClient();

	const { data, error, status } = useQuery({
		queryKey: ["stats"],
		queryFn: fetchStats,
		refetchInterval: 30000, // Refetch every 30 seconds
	});

	const {
		data: healthData,
		error: healthError,
		status: healthStatus,
	} = useQuery({
		queryKey: ["health"],
		queryFn: fetchHealth,
		refetchInterval: 60000, // Health check every minute
	});

	const updateFocusMutation = useMutation({
		mutationFn: updateFocus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["stats"] });
		},
	});

	const mapStatus = (s: string): "idle" | "loading" | "success" | "error" => {
		if (s === "pending") return "loading";
		if (s === "error") return "error";
		if (s === "success") return "success";
		return "idle";
	};

	const statsRequest: RequestState<Stats> =
		status === "success" && data
			? { status: "success", data }
			: status === "error"
				? {
						status: "error",
						message: error instanceof Error ? error.message : "Unknown error",
					}
				: { status: mapStatus(status) as "idle" | "loading" };

	const healthRequest: RequestState<HealthStatus> =
		healthStatus === "success" && healthData
			? { status: "success", data: healthData }
			: healthStatus === "error"
				? {
						status: "error",
						message:
							healthError instanceof Error
								? healthError.message
								: "Unknown error",
					}
				: { status: mapStatus(healthStatus) as "idle" | "loading" };

	return {
		statsRequest,
		healthRequest,
		updateFocus: updateFocusMutation.mutateAsync,
	};
};
